import { useState, useEffect, useCallback, useRef } from "react";
import Button from "@mui/material/Button";
import ArchiveIcon from "@mui/icons-material/Archive";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PersonAddAlt1RoundedIcon from "@mui/icons-material/PersonAddAlt1Rounded";
import PeopleOutlineRoundedIcon from "@mui/icons-material/PeopleOutlineRounded";
import { useNavigate } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import DateUtils from "utils/DateUtils.js";
import PatientService from "services/PatientService.js";
import OrganizationService from "services/OrganizationService.js";
import TableRowActionsCell from "components/utils/TableRowActionsCell.js";
import MUIDataTable from "mui-datatables";
import { ThemeProvider } from "@mui/material/styles";
import DATA_TABLE_THEME from "themes/DataTableTheme.js";
import PopUpModal from "components/utils/PopUpModal.js";
import ConfirmActionModal from "components/modals/ConfirmActionModal";
import useAPI from "hooks/useAPI.js";
import FormUtils from "utils/FormUtils";
import FileUtils from "utils/FileUtils";
import { useDispatch } from "react-redux";
import { focusHospital } from "state/slices/hospitalSlice";
import Cookies from "universal-cookie";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Alert from "@mui/material/Alert";
import OutlinedInput from "@mui/material/OutlinedInput";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import InputAdornment from "@mui/material/InputAdornment";
import styles from "styles/patients.module.css";

const cookies = new Cookies();
const ALL_CENTRES_HOSPITAL_ID = 0;

export default function PatientList() {
    const dispatch = useDispatch();
    let navigate = useNavigate();

    const [patientList, setPatientList] = useState([]);
    const popUpModalRef = useRef(null);
    const transferModalRef = useRef(null);
    const [archivingPatientId, setArchivingPatientId] = useState("");
    const [archivingPatientHospitalId, setArchivingPatientHospitalId] = useState("");
    const [transferringPatientId, setTransferringPatientId] = useState("");
    const [transferringPatientHospitalId, setTransferringPatientHospitalId] = useState("");
    const [transferringPatientName, setTransferringPatientName] = useState("");
    const [organizations, setOrganizations] = useState([]);
    const [selectedOrganizationId, setSelectedOrganizationId] = useState("");
    const [hospitals, setHospitals] = useState([]);
    const [selectedHospitalId, setSelectedHospitalId] = useState("");
    const [pagination, setPagination] = useState({
        page: 0,
        size: 10,
        total: 0,
        search: "",
    });
    const paginationRef = useRef(pagination);
    paginationRef.current = pagination;
    const skipSearchEffectRef = useRef(true);

    const fetchPatientsAPI = useAPI(
        PatientService.getAllPatients,
        fetchPatientsHandler,
        false
    );
    const archivePatientAPI = useAPI(
        PatientService.archivePatient,
        archivePatientHandler,
        true
    );
    const sendRegistrationEmailAPI = useAPI(
        PatientService.sendRegistrationEmail,
        () => { },
        true
    );
    const getActiveOrganizationsAPI = useAPI(
        OrganizationService.getActiveOrganizations,
        getActiveOrganizationsHandler,
        false
    );
    const getHospitalsByOrganizationAPI = useAPI(
        OrganizationService.getHospitalsByOrganization,
        getHospitalsByOrganizationHandler,
        false
    );
    const transferPatientAPI = useAPI(
        PatientService.transferPatient,
        () => { },
        true
    );

    const fetchPatients = useCallback(() => {
        const { page, size, search } = paginationRef.current;
        fetchPatientsAPI.execute(ALL_CENTRES_HOSPITAL_ID, page, size, search);
    }, [fetchPatientsAPI.execute]);

    const debouncedSearchRef = useRef(null);
    if (!debouncedSearchRef.current) {
        debouncedSearchRef.current = FormUtils.debounce(() => {
            const { page, size, search } = paginationRef.current;
            fetchPatientsAPI.execute(ALL_CENTRES_HOSPITAL_ID, page, size, search);
        }, 600);
    }
    const debouncedSearch = debouncedSearchRef.current;

    const dataTableOptions = {
        filter: false,
        responsive: "standard",
        selectableRows: "none",
        serverSide: true,
        fixedHeader: true,
        download: true,
        downloadOptions: {
            filename: "Patients.csv",
            filterOptions: {
                useDisplayedColumnsOnly: true,
                useDisplayedRowsOnly: true,
            },
        },
        onDownload: () => {
            downloadAllPatientsData();
            return false;
        },
        jumpToPage: true,
        count: pagination.total,
        page: pagination.page,
        rowsPerPage: pagination.size,
        searchText: pagination.search,
        searchPlaceholder: "Search by name, email, or ID",
        customSearchRender: (searchText, handleSearch, hideSearch, options) => {
            return (
                <OutlinedInput
                    value={searchText || ""}
                    onChange={(event) => handleSearch(event.target.value)}
                    placeholder="Search by name, email, or ID"
                    className={styles["patients-search-input"]}
                    startAdornment={
                        <InputAdornment position="start">
                            <SearchRoundedIcon fontSize="small" color="action" />
                        </InputAdornment>
                    }
                    size="small"
                />
            );
        },
        searchProps: {
            onKeyUp: debouncedSearch,
        },
        onChangePage: (currentPage) => {
            pagination.page = currentPage;
            setPagination((prev) => ({ ...prev, page: currentPage }));
            fetchPatients();
        },
        onChangeRowsPerPage: (rowsPerPage) => {
            pagination.size = rowsPerPage;
            setPagination((prev) => ({ ...prev, size: rowsPerPage }));
            fetchPatients();
        },
        onSearchChange: (searchText) => {
            const trimmed = searchText ? String(searchText).trim() : "";
            setPagination((prev) => ({
                ...prev,
                search: trimmed,
                page: 0,
            }));
        },
        onSearchClose: () => {
            if (pagination.search !== "") {
                setPagination((prev) => ({ ...prev, search: "", page: 0 }));
                fetchPatients();
            }
        },
        onRowClick: (_, rowMeta) => {
            const patient = patientList[rowMeta?.dataIndex];
            if (!patient?.id) return;
            const centreId = patient.associatedHospitalId ?? patient.hospitalId;
            if (centreId) {
                cookies.set("hospital", centreId, { path: "/" });
                dispatch(focusHospital({ id: centreId }));
            }
            navigate(patient.id + "/profile");
        },
        setRowProps: () => {
            return {
                className: styles["patients-table-row"],
                style: { cursor: "pointer" },
            };
        },
    };

    useEffect(() => {
        fetchPatients();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- initial load only
    }, []);

    useEffect(() => {
        if (skipSearchEffectRef.current) {
            skipSearchEffectRef.current = false;
            return;
        }
        const timer = setTimeout(() => {
            fetchPatients();
        }, pagination.search ? 600 : 0);
        return () => clearTimeout(timer);
    }, [pagination.search, fetchPatients]);

    async function downloadAllPatientsData() {
        const response = await PatientService.getAllPatients([ALL_CENTRES_HOSPITAL_ID]);

        let patientsList = response.data.content;

        // remove redundant fields
        patientsList = patientsList.map((patient) => {
            delete patient.associatedHospitalId;
            delete patient.isArchived;
            delete patient.lastUpdatedAt;
            delete patient.profilePhotoUrl;

            return patient;
        });

        FileUtils.downloadCSV(patientsList, "Patients.csv");
    }

    function archivePatient(patientId, patientHospitalId) {
        archivePatientAPI.execute(patientHospitalId, patientId);
    }

    function sendRegistrationEmail(patientId, patientHospitalId) {
        sendRegistrationEmailAPI.execute(patientHospitalId, patientId);
    }

    /* API Success Handlers */
    function fetchPatientsHandler(response) {
        let data = response.data;

        setPagination((prev) => ({ ...prev, total: data.totalElements }));

        let patients = [];
        data.content.map((patient, index) =>
            patients.push(
                transformJsonData(
                    patient,
                    index + 1
                )
            )
        );
        setPatientList(patients);
    }

    function archivePatientHandler(response) {
        window.location.reload();
    }

    function getActiveOrganizationsHandler(response) {
        setOrganizations(response.data || []);
    }

    function getHospitalsByOrganizationHandler(response) {
        const hospitalsList = response.data || [];
        setHospitals(hospitalsList);
        // Reset selected hospital when organization changes
        setSelectedHospitalId("");
    }

    function transferPatient(patientId, patientHospitalId, patientName) {
        setTransferringPatientId(patientId);
        setTransferringPatientHospitalId(patientHospitalId);
        setTransferringPatientName(patientName || "");
        setSelectedOrganizationId("");
        setSelectedHospitalId("");
        setHospitals([]);
        getActiveOrganizationsAPI.execute();
        transferModalRef.current.openModal();
    }

    function handleOrganizationChange(orgId) {
        setSelectedOrganizationId(orgId);
        setSelectedHospitalId("");
        setHospitals([]);
        if (orgId) {
            getHospitalsByOrganizationAPI.execute(orgId);
        }
    }

    function handleTransferConfirm() {
        if (!selectedOrganizationId || !selectedHospitalId || !transferringPatientHospitalId) {
            return;
        }
        transferPatientAPI.execute(transferringPatientHospitalId, transferringPatientId, {
            targetOrganizationId: selectedOrganizationId,
            targetHospitalId: selectedHospitalId,
        });
        transferModalRef.current.closeModal();
    }

    return (
        <div className={styles["patients-page"]}>
            <Box className={styles["patients-toolbar"]}>
                <Button
                    className={styles["patients-add-btn"]}
                    onClick={() => navigate("add-patient")}
                    startIcon={<PersonAddAlt1RoundedIcon />}
                    variant="contained"
                >
                    Add Patient
                </Button>
            </Box>
            <Box className={styles["patients-content"]}>
            {fetchPatientsAPI.status === "pending" ? (
                <div className="loading-state-shell text-center surface-card section-card app-card">
                    <CircularProgress
                        className="text-center"
                        sx={{
                            mt: 2,
                        }}
                    />
                </div>
            ) : patientList.length === 0 ? (
                <Box className={styles["patients-empty-state"]}>
                    <PeopleOutlineRoundedIcon className={styles["patients-empty-icon"]} />
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                        No patients found
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                        Add your first patient to start assessments, sessions, and care workflows.
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<PersonAddAlt1RoundedIcon />}
                        onClick={() => navigate("add-patient")}
                    >
                        Add Patient
                    </Button>
                </Box>
            ) : (
                <ThemeProvider theme={DATA_TABLE_THEME}>
                    <MUIDataTable
                        title={"Patients"}
                        data={patientList}
                        columns={dataTableColumns}
                        options={dataTableOptions}
                        className={`surface-card section-card app-card ${styles["patients-data-table"]}`}
                    />
                </ThemeProvider>
            )}
            </Box>
            <PopUpModal ref={popUpModalRef} modalWidth={400}>
                <ConfirmActionModal
                    content="Do you want to confirm archiving the patient?"
                    confirmActionHandler={() => {
                        archivePatient(archivingPatientId, archivingPatientHospitalId);
                        popUpModalRef.current.closeModal();
                    }}
                    onCloseHandler={() => popUpModalRef.current.closeModal()}
                />
            </PopUpModal>
            <PopUpModal ref={transferModalRef} modalWidth={500}>
                <Box
                    className="px-3 py-4"
                    sx={{
                        maxWidth: "450px",
                    }}
                >
                    <Typography id="modal-modal-title" variant="h3" color="text" sx={{ mb: 2 }}>
                        Transfer '{transferringPatientName || "Patient"}' to Another Organization
                    </Typography>
                    <Alert severity="warning" sx={{ mb: 3 }}>
                        <strong>Caution:</strong> Transferring a patient will create a new record in the selected organization and center, and
                        their data including profile, documents, biometrics, intake packs and registration forms, and assessment and stim forms
                        will be copied. This action will notify the patient via email. Please ensure you have selected the correct organization
                        and center.
                    </Alert>
                    <FormControl fullWidth sx={{ mb: 3 }}>
                        <InputLabel id="organization-select-label">Select Organization</InputLabel>
                        <Select
                            labelId="organization-select-label"
                            id="organization-select"
                            value={selectedOrganizationId}
                            label="Select Organization"
                            onChange={(e) => handleOrganizationChange(e.target.value)}
                        >
                            {organizations.map((org) => (
                                <MenuItem key={org.id} value={org.id}>
                                    {org.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {selectedOrganizationId && (
                        <FormControl fullWidth sx={{ mb: 3 }}>
                            <InputLabel id="hospital-select-label">Select Hospital</InputLabel>
                            <Select
                                labelId="hospital-select-label"
                                id="hospital-select"
                                value={selectedHospitalId}
                                label="Select Hospital"
                                onChange={(e) => setSelectedHospitalId(e.target.value)}
                                disabled={hospitals.length === 0 || getHospitalsByOrganizationAPI.status === "pending"}
                            >
                                {hospitals.map((hospital) => (
                                    <MenuItem key={hospital.id} value={hospital.id}>
                                        {hospital.hospitalName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                    <Stack direction="row" spacing={3} className="mt-4">
                        <Button
                            onClick={handleTransferConfirm}
                            variant="contained"
                            disabled={!selectedOrganizationId || !selectedHospitalId || transferPatientAPI.status === "pending"}
                        >
                            {transferPatientAPI.status === "pending" ? "Transferring..." : "Transfer"}
                        </Button>
                        <Button
                            onClick={() => transferModalRef.current.closeModal()}
                            variant="outlined"
                        >
                            Cancel
                        </Button>
                    </Stack>
                </Box>
            </PopUpModal>
        </div>
    );

    function transformJsonData(
        patient,
        index
    ) {
        return {
            id: patient.id,
            hospitalId: patient.associatedHospitalId,
            index,
            fullName: (
                <Typography
                    variant="body2"
                    className={`${styles["patients-name"]} ${styles["patients-secondary-text"]}`}
                >
                    {patient.firstName + " " + patient.lastName}
                </Typography>
            ),
            phone: (
                <Typography variant="body2" className={styles["patients-secondary-text"]}>
                    {patient.phone || "-"}
                </Typography>
            ),
            email: (
                <Typography variant="body2" className={styles["patients-secondary-text"]}>
                    {patient.email || "-"}
                </Typography>
            ),
            centre: (
                <Typography variant="body2" className={styles["patients-secondary-text"]}>
                    {patient.associatedHospitalName || "-"}
                </Typography>
            ),
            dob: patient.dob,
            createdAt: DateUtils.formatDateTime(patient.createdAt),
            actions: (
                <ActionsCell
                    patientId={patient.id}
                    hospitalId={patient.associatedHospitalId}
                    patientName={patient.firstName + " " + patient.lastName}
                />
            ),
        };
    }

    function ActionsCell({ patientId, hospitalId, patientName }) {
        const actions = {
            startItems: [],
            menuItems: [
                {
                    label: "Archive Patient",
                    icon: <ArchiveIcon fontSize="small" color="warning" />,
                    onClickHandler: () => {
                        setArchivingPatientId(patientId);
                        setArchivingPatientHospitalId(hospitalId);
                        popUpModalRef.current.openModal();
                    },
                },
                {
                    label: "Send Registration Email",
                    onClickHandler: () => sendRegistrationEmail(patientId, hospitalId),
                },
                {
                    label: "Transfer Patient",
                    onClickHandler: () => transferPatient(patientId, hospitalId, patientName),
                },
            ],
            menuIcon: <MoreVertIcon fontSize="small" color="action" />,
            menuLabel: "More patient actions",
        };

        return <TableRowActionsCell actions={actions} />;
    }
}

const dataTableColumns = [
    {
        name: "id",
        label: "Patient ID",
        options: {
            filter: true,
            sort: false,
        },
    },
    {
        name: "fullName",
        label: "Full Name",
        options: {
            filter: true,
            sort: false,
        },
    },
    {
        name: "email",
        label: "Email ID",
        options: {
            filter: true,
            sort: false,
        },
    },
    {
        name: "phone",
        label: "Phone",
        options: {
            filter: true,
            sort: false,
        },
    },
    {
        name: "centre",
        label: "Centre",
        options: {
            filter: true,
            sort: false,
        },
    },
    {
        name: "createdAt",
        label: "Created At",
        options: {
            filter: true,
            sort: false,
        },
    },
    {
        name: "actions",
        label: "Actions",
        options: {
            filter: false,
            sort: false,
            download: false,
        },
    },
];
